import { z } from 'zod';

import { BINARY_ANSWERS, type BinaryAnswer } from '@/constants/answers';
import {
  type ActiveEntityCriterion,
  BeneficialOwnerType,
  CONTROLLING_PERSON_TYPES,
  CONTROLLING_PERSON_TYPES_BY_ENTITY_KIND,
  ControllingPersonType,
  ENTITY_KINDS,
  EntityClassification,
  type EntityKind,
  ExcludedPersonType,
  LEGAL_FORMS,
  type LegalForm,
  LISTED_CORPORATION_RELATIONSHIPS,
  ListedCorporationRelationship,
  NoTinReason,
  RegulatedMarketListing,
} from '@/constants/institution';
import {
  INSTITUTION_LISTED_COMPANY_DEFAULTS,
  type InstitutionListedCompany,
  institutionListedCompanySchema,
  withListedCompanyRefinements,
} from '@/features/institution/schemas/listed-company';
import {
  type InstitutionPerson,
  institutionDetailsPersonApiToInstitutionPerson,
  institutionPersonSchema,
  institutionPersonToInstitutionDetailsPersonApi,
} from '@/features/institution/schemas/person';
import i18n from '@/lib/i18n';
import { getCountryIsEu, mainstreamCountrySchema } from '@/services/api/mainstream';
import {
  entityKindsOf,
  type InstitutionDetailsV2ControllingPerson,
  type InstitutionDetailsV2EntityTaxResidency,
  type InstitutionDetailsV2Partial,
  type InstitutionDetailsV2TaxSelfCertification,
  type InstitutionDetailsV2TaxSelfCertificationSubmission,
  institutionDetailsV2ActiveEntityCriterionSchema,
  institutionDetailsV2BeneficiaryClassSchema,
  institutionDetailsV2ControllingPersonSchema,
  institutionDetailsV2EntityTinSchema,
  institutionDetailsV2ListedCompanyMicSchema,
  institutionDetailsV2ListedCompanyNameSchema,
  institutionDetailsV2TaxSelfCertificationSubmissionSchema,
  LEGAL_ARRANGEMENT_ROLE_TYPES,
  type LegalArrangementKind,
} from '@/services/api/profile';
import { addFieldIssue } from '@/utils/field-issues';
import { EMPTY_PHONE_NUMBER } from '@/utils/phone-number';
import { hasUnique } from '@/utils/refinements';

export const TAX_RESIDENCIES_MAX = 5;

export const CONTROLLING_PERSONS_MAX = 20;

export const BENEFICIARY_CLASSES_MAX = 5;

type InstitutionEntityTaxResidency = {
  jurisdiction: string;
  hasNoTin: boolean;
  tin: string;
  noTinReason?: NoTinReason;
};

type InstitutionControllingPerson = InstitutionPerson & {
  controllingPersonTypes: ControllingPersonType[];
  isPep?: BinaryAnswer;
};

export type InstitutionTaxSelfCertification = {
  noTaxResidence?: BinaryAnswer;
  taxResidencies: InstitutionEntityTaxResidency[];
  placeOfEffectiveManagement: InstitutionEntityTaxResidency;
  excludedPersonType?: ExcludedPersonType;
  listedCompany: InstitutionListedCompany;
  relationship?: ListedCorporationRelationship;
  controlOverHalf: boolean;
  entityClassification?: EntityClassification;
  activeEntityCriterion?: ActiveEntityCriterion;
  controllingPersons: InstitutionControllingPerson[];
  // The company section's answer, which decides the roles a controlling person can hold
  legalForm?: LegalForm;
  // Asked only where the legal form doesn't settle it, and read back from the roles as it isn't saved
  entityKind?: EntityKind;
  hasProtector?: BinaryAnswer;
  beneficiaryClasses: Array<{ description: string }>;
  declaration: {
    authorisedToSign: boolean;
    accurate: boolean;
    consentToReporting: boolean;
    undertakeToNotifyChanges: boolean;
  };
  signatory: {
    partyId?: string;
    fullName: string;
    capacity: string;
  };
};

export type InstitutionTaxSelfCertificationOrDefaults = InstitutionTaxSelfCertification | Partial<InstitutionTaxSelfCertification>;

export const INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS: InstitutionEntityTaxResidency = {
  jurisdiction: '',
  hasNoTin: false,
  tin: '',
  noTinReason: undefined,
};

export const INSTITUTION_TAX_SELF_CERTIFICATION_SIGN_OFF_DEFAULTS: Pick<InstitutionTaxSelfCertification, 'declaration' | 'signatory'> = {
  declaration: { authorisedToSign: false, accurate: false, consentToReporting: false, undertakeToNotifyChanges: false },
  signatory: { fullName: '', capacity: '' },
};

const hasUniquePartyIds = hasUnique('partyId');

const institutionEntityTaxResidencySchema = (jurisdictionError: string) =>
  z
    .object({
      jurisdiction: mainstreamCountrySchema(jurisdictionError),
      hasNoTin: z.boolean(),
      tin: z.string(),
      noTinReason: z.enum(NoTinReason).optional(),
    })
    .superRefine(async (residency, context) => {
      if (!residency.hasNoTin) {
        addFieldIssue(context, ['tin'], institutionDetailsV2EntityTinSchema, residency.tin);

        return;
      }

      // NL implementation (Besluit art. 3(2)): the no-TIN exception covers non-EU residences only
      if (await getCountryIsEu(residency.jurisdiction)) {
        context.addIssue({ code: 'custom', path: ['hasNoTin'], message: i18n.t('institutionDetails.taxResidency.errors.tinRequiredForEuResidence') });
      }

      if (!residency.noTinReason) {
        context.addIssue({ code: 'custom', path: ['noTinReason'], message: i18n.t('institutionDetails.taxResidency.errors.noTinReason') });
      }
    });

export const institutionTaxSelfCertificationResidencySchema = z.discriminatedUnion(
  'noTaxResidence',
  [
    z.object({
      noTaxResidence: z.literal('NO'),
      taxResidencies: z
        .array(institutionEntityTaxResidencySchema(i18n.t('institutionDetails.taxResidency.errors.jurisdiction')))
        .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.taxResidencies'))
        .max(TAX_RESIDENCIES_MAX, i18n.t('institutionDetails.taxSelfCertification.errors.taxResidenciesTooMany'))
        .refine(hasUnique('jurisdiction'), {
          error: i18n.t('institutionDetails.taxSelfCertification.errors.taxResidenciesDuplicate'),
          when: ({ issues }) => issues.length === 0,
        }),
    }),
    z.object({
      noTaxResidence: z.literal('YES'),
      placeOfEffectiveManagement: institutionEntityTaxResidencySchema(i18n.t('institutionDetails.taxSelfCertification.errors.placeOfEffectiveManagement')),
    }),
  ],
  { error: i18n.t('institutionDetails.taxSelfCertification.errors.noTaxResidence') },
);

export const institutionTaxSelfCertificationClassificationSchema = z.discriminatedUnion(
  'excludedPersonType',
  [
    z.object({
      excludedPersonType: z.enum([
        ExcludedPersonType.GOVERNMENTAL_ENTITY,
        ExcludedPersonType.INTERNATIONAL_ORGANISATION,
        ExcludedPersonType.CENTRAL_BANK,
        ExcludedPersonType.FINANCIAL_INSTITUTION,
      ]),
    }),
    z.object({
      excludedPersonType: z.literal(ExcludedPersonType.LISTED_CORPORATION),
      listedCompany: withListedCompanyRefinements(institutionListedCompanySchema.extend({ mic: institutionDetailsV2ListedCompanyMicSchema })),
    }),
    z.object({
      excludedPersonType: z.literal(ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION),
      listedCompany: withListedCompanyRefinements(
        institutionListedCompanySchema.extend({ name: institutionDetailsV2ListedCompanyNameSchema, mic: institutionDetailsV2ListedCompanyMicSchema }),
      ),
      relationship: z.enum(LISTED_CORPORATION_RELATIONSHIPS, { error: i18n.t('institutionDetails.taxSelfCertification.errors.relationship') }),
      controlOverHalf: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.controlOverHalf') }),
    }),
    z.discriminatedUnion(
      'entityClassification',
      [
        z.object({
          excludedPersonType: z.literal(ExcludedPersonType.NONE),
          entityClassification: z.literal(EntityClassification.ACTIVE_ENTITY),
          activeEntityCriterion: institutionDetailsV2ActiveEntityCriterionSchema,
        }),
        z.object({
          excludedPersonType: z.literal(ExcludedPersonType.NONE),
          entityClassification: z.literal(EntityClassification.PASSIVE_ENTITY),
        }),
      ],
      { error: i18n.t('institutionDetails.taxSelfCertification.errors.entityClassification') },
    ),
  ],
  { error: i18n.t('institutionDetails.taxSelfCertification.errors.excludedPersonType') },
);

const entityKindsOfLegalForm = (legalForm: LegalForm | undefined) => (legalForm ? entityKindsOf(legalForm) : ENTITY_KINDS);

export const isEntityKindAsked = (legalForm: LegalForm | undefined) => entityKindsOfLegalForm(legalForm).length > 1;

export const isLegalArrangementKind = (entityKind: EntityKind | undefined): entityKind is LegalArrangementKind =>
  entityKind !== undefined && entityKind in LEGAL_ARRANGEMENT_ROLE_TYPES;

// The kind whose roles were all chosen, where the legal form leaves more than one open
const entityKindOf = (legalForm: LegalForm | undefined, types: ControllingPersonType[]) => {
  const kinds = entityKindsOfLegalForm(legalForm);
  if (kinds.length === 1) return kinds[0];

  return types.length ? kinds.find((kind) => types.every((type) => CONTROLLING_PERSON_TYPES_BY_ENTITY_KIND[kind].includes(type))) : undefined;
};

// Only the roles of the entity's kind have checkboxes, so roles of another kind would block the step with no way to untick them
export const withRolesOfKind = (controllingPersons: InstitutionControllingPerson[], kind: EntityKind) =>
  controllingPersons.map((person) => ({
    ...person,
    controllingPersonTypes: person.controllingPersonTypes.filter((type) => CONTROLLING_PERSON_TYPES_BY_ENTITY_KIND[kind].includes(type)),
  }));

const requireEntityKind = ({ legalForm, entityKind }: Pick<InstitutionTaxSelfCertification, 'legalForm' | 'entityKind'>, context: z.RefinementCtx) => {
  if (entityKind || !isEntityKindAsked(legalForm)) return;

  context.addIssue({ code: 'custom', path: ['entityKind'], message: i18n.t('institutionDetails.taxSelfCertification.errors.entityKind') });
};

const requireControllingPersonTypesOfKind = (
  { entityKind, controllingPersons }: Pick<InstitutionTaxSelfCertification, 'entityKind' | 'controllingPersons'>,
  context: z.RefinementCtx,
) => {
  if (!entityKind) return;

  controllingPersons.forEach(({ controllingPersonTypes }, index) => {
    if (controllingPersonTypes.every((type) => CONTROLLING_PERSON_TYPES_BY_ENTITY_KIND[entityKind].includes(type))) return;

    context.addIssue({
      code: 'custom',
      path: ['controllingPersons', index, 'controllingPersonTypes'],
      message: i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonTypesNotOfLegalForm'),
    });
  });
};

// The roles fast-api requires of a trust or other legal arrangement on completion, asked here where they can be answered
const requireLegalArrangementRoles = (
  {
    entityKind,
    hasProtector,
    beneficiaryClasses,
    controllingPersons,
  }: Pick<InstitutionTaxSelfCertification, 'entityKind' | 'hasProtector' | 'beneficiaryClasses' | 'controllingPersons'>,
  context: z.RefinementCtx,
) => {
  if (!isLegalArrangementKind(entityKind)) return;

  const roleTypes = LEGAL_ARRANGEMENT_ROLE_TYPES[entityKind];
  const types = controllingPersons.flatMap(({ controllingPersonTypes }) => controllingPersonTypes);
  const addIssue = (path: PropertyKey[], key: string) => context.addIssue({ code: 'custom', path, message: i18n.t(`institutionDetails.taxSelfCertification.errors.${key}`) });

  if (!types.includes(roleTypes.SETTLOR)) addIssue(['controllingPersons'], 'settlorRequired');
  if (!types.includes(roleTypes.TRUSTEE)) addIssue(['controllingPersons'], 'trusteeRequired');
  if (!hasProtector) addIssue(['hasProtector'], 'hasProtector');
  if (hasProtector === 'YES' && !types.includes(roleTypes.PROTECTOR)) addIssue(['controllingPersons'], 'protectorRequired');
  if (hasProtector === 'NO' && types.includes(roleTypes.PROTECTOR)) addIssue(['controllingPersons'], 'protectorNotExpected');
  if (!types.includes(roleTypes.BENEFICIARY) && !beneficiaryClasses.length) addIssue(['controllingPersons'], 'beneficiaryRequired');

  beneficiaryClasses.forEach(({ description }, index) => {
    addFieldIssue(context, ['beneficiaryClasses', index, 'description'], institutionDetailsV2BeneficiaryClassSchema, description);
  });
};

export const institutionTaxSelfCertificationControllingPersonsSchema = z
  .object({
    legalForm: z.enum(LEGAL_FORMS).optional(),
    entityKind: z.enum(ENTITY_KINDS).optional(),
    hasProtector: z.enum(BINARY_ANSWERS).optional(),
    beneficiaryClasses: z
      .array(z.object({ description: z.string() }))
      .max(BENEFICIARY_CLASSES_MAX, i18n.t('institutionDetails.taxSelfCertification.errors.beneficiaryClassesTooMany')),
    controllingPersons: z
      .array(
        // Contact details are not asked for, and only carried for people prefilled from the ownership section
        institutionPersonSchema.extend({
          email: z.string(),
          phoneNumber: z.object({ areaCode: z.string(), mobilePhone: z.string() }),
          controllingPersonTypes: z.array(z.enum(CONTROLLING_PERSON_TYPES)).min(1, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonTypes')),
          isPep: institutionDetailsV2ControllingPersonSchema.shape.isPep,
        }),
      )
      .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersons'))
      .max(CONTROLLING_PERSONS_MAX, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonsTooMany'))
      .refine(hasUniquePartyIds, { error: i18n.t('institutionDetails.taxSelfCertification.errors.duplicateParty'), when: ({ issues }) => issues.length === 0 }),
  })
  .superRefine(requireEntityKind)
  .superRefine(requireControllingPersonTypesOfKind)
  .superRefine(requireLegalArrangementRoles);

export const institutionTaxSelfCertificationDeclarationSchema = z.object({
  declaration: z.object({
    authorisedToSign: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
    accurate: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
    consentToReporting: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
    undertakeToNotifyChanges: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
  }),
  signatory: z.object({
    partyId: z.uuid(i18n.t('institutionDetails.taxSelfCertification.errors.signatory')),
    fullName: z.string().min(1, i18n.t('institutionDetails.taxSelfCertification.errors.signatoryFullName')),
    capacity: z.string().min(1, i18n.t('institutionDetails.taxSelfCertification.errors.signatoryCapacity')),
  }),
});

export const isControllingPersonsApplicable = ({ excludedPersonType, entityClassification }: InstitutionTaxSelfCertificationOrDefaults) =>
  excludedPersonType === ExcludedPersonType.NONE && entityClassification === EntityClassification.PASSIVE_ENTITY;

const institutionEntityTaxResidencyToInstitutionDetailsEntityTaxResidencyApi = ({ jurisdiction, hasNoTin, tin, noTinReason }: InstitutionEntityTaxResidency) =>
  hasNoTin ? { jurisdiction, noTinReason } : { jurisdiction, tin };

const institutionDetailsEntityTaxResidencyApiToInstitutionEntityTaxResidency = (residency: InstitutionDetailsV2EntityTaxResidency): InstitutionEntityTaxResidency => ({
  jurisdiction: residency.jurisdiction,
  hasNoTin: residency.noTinReason !== undefined,
  tin: residency.tin ?? '',
  noTinReason: residency.noTinReason,
});

const institutionControllingPersonToInstitutionDetailsControllingPersonApi = ({ controllingPersonTypes, isPep, ...person }: InstitutionControllingPerson) => ({
  ...institutionPersonToInstitutionDetailsPersonApi(person),
  controllingPersonTypes,
  isPep,
});

const institutionDetailsControllingPersonApiToInstitutionControllingPerson = ({
  controllingPersonTypes,
  isPep,
  ...person
}: InstitutionDetailsV2ControllingPerson): InstitutionControllingPerson => ({
  ...institutionDetailsPersonApiToInstitutionPerson({ ...person, email: '', phoneNumber: EMPTY_PHONE_NUMBER }),
  controllingPersonTypes,
  isPep,
});

// Parsing drops the answers left over from an earlier choice, and the contact details of people prefilled from the ownership section
export const institutionTaxSelfCertificationToInstitutionDetailsTaxSelfCertificationApi = ({
  taxResidencies,
  placeOfEffectiveManagement,
  controllingPersons,
  entityKind,
  hasProtector,
  beneficiaryClasses,
  ...taxSelfCertification
}: InstitutionTaxSelfCertification): Promise<InstitutionDetailsV2TaxSelfCertificationSubmission> =>
  institutionDetailsV2TaxSelfCertificationSubmissionSchema.parseAsync({
    ...taxSelfCertification,
    taxResidencies: taxResidencies.map(institutionEntityTaxResidencyToInstitutionDetailsEntityTaxResidencyApi),
    placeOfEffectiveManagement: institutionEntityTaxResidencyToInstitutionDetailsEntityTaxResidencyApi(placeOfEffectiveManagement),
    controllingPersons: controllingPersons.map(institutionControllingPersonToInstitutionDetailsControllingPersonApi),
    // Only a trust or other legal arrangement answers these, so another kind chosen earlier leaves none behind
    ...(isLegalArrangementKind(entityKind) && {
      hasProtector,
      beneficiaryClasses: beneficiaryClasses.length ? beneficiaryClasses.map(({ description }) => description) : undefined,
    }),
  });

const isSignatoryInManagement = ({ signatory }: InstitutionDetailsV2TaxSelfCertification, ownership: InstitutionDetailsV2Partial['ownership']) =>
  (ownership?.management ?? []).some(({ partyId }) => partyId === signatory.partyId);

// Each save dates a new self-certification, so it's declared afresh
export const institutionDetailsTaxSelfCertificationApiToInstitutionTaxSelfCertification = (
  taxSelfCertification: InstitutionDetailsV2TaxSelfCertification,
  answers: InstitutionDetailsV2Partial | null | undefined,
): InstitutionTaxSelfCertification => {
  const controllingPersons =
    'controllingPersons' in taxSelfCertification ? taxSelfCertification.controllingPersons.map(institutionDetailsControllingPersonApiToInstitutionControllingPerson) : [];
  const entityKind = entityKindOf(
    answers?.company?.legalForm,
    controllingPersons.flatMap(({ controllingPersonTypes }) => controllingPersonTypes),
  );

  return {
    noTaxResidence: taxSelfCertification.noTaxResidence,
    taxResidencies: 'taxResidencies' in taxSelfCertification ? taxSelfCertification.taxResidencies.map(institutionDetailsEntityTaxResidencyApiToInstitutionEntityTaxResidency) : [],
    placeOfEffectiveManagement:
      'placeOfEffectiveManagement' in taxSelfCertification
        ? institutionDetailsEntityTaxResidencyApiToInstitutionEntityTaxResidency(taxSelfCertification.placeOfEffectiveManagement)
        : { ...INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS },
    excludedPersonType: taxSelfCertification.excludedPersonType,
    listedCompany:
      'listedCompany' in taxSelfCertification ? { ...INSTITUTION_LISTED_COMPANY_DEFAULTS, ...taxSelfCertification.listedCompany } : INSTITUTION_LISTED_COMPANY_DEFAULTS,
    relationship: 'relationship' in taxSelfCertification ? taxSelfCertification.relationship : undefined,
    controlOverHalf: 'controlOverHalf' in taxSelfCertification ? taxSelfCertification.controlOverHalf : false,
    entityClassification: 'entityClassification' in taxSelfCertification ? taxSelfCertification.entityClassification : undefined,
    activeEntityCriterion: 'activeEntityCriterion' in taxSelfCertification ? taxSelfCertification.activeEntityCriterion : undefined,
    // A change of legal form can leave roles of the kind the entity was
    controllingPersons: entityKind ? withRolesOfKind(controllingPersons, entityKind) : controllingPersons,
    legalForm: answers?.company?.legalForm,
    entityKind,
    hasProtector: 'hasProtector' in taxSelfCertification ? taxSelfCertification.hasProtector : undefined,
    beneficiaryClasses: 'beneficiaryClasses' in taxSelfCertification ? (taxSelfCertification.beneficiaryClasses ?? []).map((description) => ({ description })) : [],
    declaration: INSTITUTION_TAX_SELF_CERTIFICATION_SIGN_OFF_DEFAULTS.declaration,
    signatory: isSignatoryInManagement(taxSelfCertification, answers?.ownership) ? taxSelfCertification.signatory : INSTITUTION_TAX_SELF_CERTIFICATION_SIGN_OFF_DEFAULTS.signatory,
  };
};

// The listing an exclusion rests on, where the ownership section gives it: the company's own, or that of the listed company owning it
export const getInstitutionListedCompanyPrefill = (
  excludedPersonType: ExcludedPersonType | undefined,
  ownership: InstitutionDetailsV2Partial['ownership'],
): Pick<InstitutionTaxSelfCertification, 'listedCompany' | 'relationship'> | undefined => {
  if (!ownership || !('listedCompany' in ownership)) return undefined;

  const listedCompany = { ...INSTITUTION_LISTED_COMPANY_DEFAULTS, ...ownership.listedCompany };

  if (excludedPersonType === ExcludedPersonType.LISTED_CORPORATION && ownership.regulatedMarketListing === RegulatedMarketListing.LISTED) {
    return { listedCompany, relationship: undefined };
  }

  if (excludedPersonType === ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION && ownership.regulatedMarketListing === RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY) {
    return { listedCompany, relationship: ListedCorporationRelationship.CONTROLLED_BY_LISTED_CORPORATION };
  }

  return undefined;
};

// The UBOs, then the management who are not also UBOs, keeping each person's partyId. A UBO's role follows from how they own or control
// a company, which says nothing of their role in a trust or other legal arrangement
export const getInstitutionControllingPersonCandidates = (
  ownership: InstitutionDetailsV2Partial['ownership'],
  entityKind: EntityKind | undefined,
): InstitutionControllingPerson[] => {
  const owners = ownership && 'ubos' in ownership ? ownership.ubos : [];
  const management = (ownership?.management ?? []).filter(({ partyId }) => !owners.some((owner) => owner.partyId === partyId));

  return [
    ...owners.map(({ type, isPep, ...owner }) => ({
      ...institutionDetailsPersonApiToInstitutionPerson(owner),
      controllingPersonTypes:
        entityKind === 'LEGAL_PERSON' ? [type === BeneficialOwnerType.OWNER ? ControllingPersonType.OWNERSHIP_INTEREST : ControllingPersonType.SENIOR_MANAGING_OFFICIAL] : [],
      isPep,
    })),
    ...management.map(({ roles, isPep, ...person }) => ({ ...institutionDetailsPersonApiToInstitutionPerson(person), controllingPersonTypes: [], isPep })),
  ];
};

// The tax residence isn't prefilled from the company's answers: CARF Commentary III para 45 leaves it to the customer's own declaration
export const createInstitutionTaxSelfCertificationDefaults = (answers: InstitutionDetailsV2Partial | null | undefined): InstitutionTaxSelfCertification => {
  return {
    taxResidencies: [{ ...INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS }],
    placeOfEffectiveManagement: { ...INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS },
    listedCompany: INSTITUTION_LISTED_COMPANY_DEFAULTS,
    controlOverHalf: false,
    controllingPersons: [],
    legalForm: answers?.company?.legalForm,
    entityKind: entityKindOf(answers?.company?.legalForm, []),
    beneficiaryClasses: [],
    ...INSTITUTION_TAX_SELF_CERTIFICATION_SIGN_OFF_DEFAULTS,
  };
};
